/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { HotToastService } from '@ngneat/hot-toast';
import { badgeColorCode, BadgeOptions } from '../../badge/badge.interface';
import { DataCollectorService } from '../../../core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from '../../../core/services/localisation/zuper.localisation.service';
import { UtilityService } from '../../../core/services/utility_functions/utility_functions.service';
import { PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';
import { MultiModulePickerService } from 'app/components/multi-module-picker/multi-module-picker.service';

@Component({
  selector: 'invoice-data-picker',
  templateUrl: './invoice-data-picker.component.html',
  styleUrls: ['./invoice-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoiceDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedInvoices: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() invoiceUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'created_at';
  @Input() sortType: string = 'DESC';
  @Input() customParams: any = null;
  @Input() isFromNote: boolean = false;
  @Input() module: string = '';
  @Input() moduleUID: string = '';
  invoices: any[] = [];
  invoicesList: any[] = [];
  invoiceEleId: string = '';
  invoiceDetails: any = null;
  loaded: boolean = false;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  isSpinLoading: boolean = false;
  currency: string;
  digitsInfo: string = '1.2-2';
  associatedInvoices: any[] = [];
  isAssociatedExpanded: boolean = true;
  notesPickerPreferences: any = {};

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private multiModulePickerService: MultiModulePickerService,
    private toast: HotToastService) {
  }

  ngOnInit(): void {
    this.notesPickerPreferences = this.multiModulePickerService.notesPickerPreferences;
    this.currency = this.utilityService.companyCurrency;
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.showDataPickerPanel();
  }

  rotateArrowIcon(invoiceUID: any, isAssociated?: boolean): any {
    const isExpanded: boolean = this.isExpandingAction(invoiceUID, isAssociated);
    return {
      'rotate-arrow-icon-down': isExpanded,
      'rotate-arrow-icon-up': !isExpanded
    };
  }

  showSpinLoading(invoiceUID: any, isAssociated?: boolean): boolean {
    return this.isExpandingAction(invoiceUID, isAssociated) && this.isSpinLoading;
  }

  updateArrowTooltipText(invoiceUID: any, isAssociated?: boolean): string {
    return this.isExpandingAction(invoiceUID, isAssociated) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(invoiceUID: any, isAssociated?: boolean): boolean {
    return (this.invoiceEleId === 'invoice_details_' + invoiceUID && !isAssociated) || (this.invoiceEleId === 'invoice_details_associated_' + invoiceUID && isAssociated);
  }

  viewInvoiceFullDetails(invoiceUID: string): string {
    return '/invoices/' + invoiceUID + '/details';
  }

  getStatusBadgeOptions(type: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: type,
      displayType: type,
      color: badgeColorCode[type],
      displayKey: null,
      displayName: this.getProperStatusText(type)
    };
    return badgeOption;
  }

  getProperStatusText(status: string): string {
    if (status === 'AWAIT_PAYMENT') {
      return 'SENT';
    }
    return status ? status.split('_').join(' ') : '';
  }

  viewInvoiceDetails(event: any, invoiceUID: string, isAssociated?: boolean): void {
    event.stopPropagation();
    const elementId: string = (isAssociated ? 'invoice_details_associated_' : 'invoice_details_') + invoiceUID;
    if (this.invoiceEleId === elementId) {
      this.resetInvoiceDetails();
    } else {
      this.invoiceEleId = elementId;
      this.getInvoiceDetails(invoiceUID);
    }
  }

  getInvoiceDetails(invoiceUID: string): void {
    this.isSpinLoading = true;
    this.dataCollectorService.getData(this.invoiceUrl + '/' + invoiceUID).subscribe(
      (invoices: any) => {
        this.invoiceDetails = invoices.data;
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.resetInvoiceDetails();
        this.isSpinLoading = false;
      });
  }

  resetInvoiceDetails(): void {
    this.invoiceEleId = '';
    this.invoiceDetails = null;
    this.changeDetectorRef.detectChanges();
  }

  isSelectedInvoice(invoice: any): boolean {
    if (this.selectedInvoices?.length) {
      return this.selectedInvoices.find(value => value.invoice_uid === invoice.invoice_uid) ? true : false;
    }
    return false;
  }

  selectInvoice(event: any, invoice: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event?.target?.checked === true) {
        this.selectedInvoices.push(invoice);
      } else {
        this.selectedInvoices = this.selectedInvoices.filter(value => value.invoice_uid !== invoice.invoice_uid);
      }
    } else {
      this.selectedInvoices = [invoice];
    }
    this.eventEmitter.emit({ value: this.selectedInvoices });
  }

  triggerSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    this.onSearch = true;
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex??0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.invoiceEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);
    if(this.notesPickerPreferences[this.module]?.preference?.invoice === 'LINKED'){
      urlParams.updates.push({
        "param": this.notesPickerPreferences[this.module]?.filter,
        "value": this.moduleUID,
        "op": "s"
      });
    } else if(this.notesPickerPreferences[this.module]?.preference?.invoice === 'BOTH' && this.isFromNote){
      const urlParams =  {
        'sort': 'DESC',
        'sort_by': 'created_at',
        'filter.is_active': true,
        'page': 1,
        'count': 20,
        'filter.keyword':  this.defaultParamObj['filter.keyword'],
      };
      this.getAssociatedData(urlParams);
    }
    this.dataCollectorService.getData(this.invoiceUrl, urlParams).subscribe(
      (response: any) => {
        this.invoicesList = response.data;
        this.invoices = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.invoices.length) {
          this.selectedInvoices = this.selectedInvoices?.length > 0 ? _.uniqBy([...this.selectedInvoices, ...this.filter], 'invoice_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedInvoices });
        } else if (isOpen && this.invoices.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.loaded = true;
      });
  }

  getAssociatedData(urlParams): void {
    urlParams[this.notesPickerPreferences[this.module]?.filter] = this.moduleUID;
    this.dataCollectorService.getData(this.invoiceUrl, urlParams).subscribe(
      (response: any) => {
        this.associatedInvoices = response.data;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
      });
  }

  showDataPickerContent(): void {
    const pageIndex: number = this.pageIndex??0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      if (this.filter[0].invoice_no) {
        this.defaultParamObj[this.pickerConfig.params.searchKey] = this.filter[0].invoice_no;
        this.eventEmitter.emit({ searchKey: this.filter[0].invoice_no });
      } else {
        this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].invoice_uid;
      }
    }
  }

  resetInvoiceDataPicker(): void {
    this.invoices = [];
  }

  showDataPickerPanel(): void {
    this.resetInvoiceDataPicker();
    if (this.showSelectedItems) {
      this.invoices = this.selectedInvoices;
    } else if (this.invoicesList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.invoicesList.length > 0 || this.searchKey) {
      this.invoices = this.invoicesList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedInvoices, filter: this.selectedInvoices });
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedInvoices ? this.selectedInvoices.length : 0,
      displayType: 'listingCount'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.isAssociatedExpanded = false;
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }
}
